import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, DollarSign, Home, Building, PiggyBank, CreditCard, Globe, Calculator, FileText, Target, AlertTriangle, CheckCircle, Clock, BarChart3, ChevronRight, X, Wallet, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/apiClient';
import StructuredData from '../components/StructuredData';
import CanonicalURL from '../components/CanonicalURL';
import Disclaimer from '../components/Disclaimer';

const Dashboard = () => {
  const { user } = useAuth();
  const [showAssetBreakdown, setShowAssetBreakdown] = useState(false);
  const [showNetAssetBreakdown, setShowNetAssetBreakdown] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    // Tax Summary
    currentYearTax: 0,
    projectedTax: 0,
    taxSavings: 0,
    
    // Real Estate Portfolio
    properties: [],
    
    // Roth IRA Status
    rothBalance: 0,
    rothContributions: 0,
    rothConversions: 0,
    rothEarnings: 0,
    yearsUntilQualified: 18.5,
    
    // Account Overview
    accounts: [],
    
    // Recent Activity
    recentExpenses: [
      { description: 'Property Tax - Rental 1', amount: 2400, category: 'property_taxes', date: '2024-01-15' },
      { description: 'Insurance - Rental 2', amount: 1800, category: 'insurance', date: '2024-01-10' },
      { description: 'Maintenance - Primary', amount: 1200, category: 'maintenance', date: '2024-01-05' }
    ],
    
    // Tax Optimization Opportunities
    opportunities: [
      { type: 'roth_conversion', description: 'Convert traditional IRA during low-income year', potential: 5000 },
      { type: 'real_estate', description: 'Maximize rental property expense tracking', potential: 3000 },
      { type: 'family_credits', description: 'Child tax credit and childcare expenses', potential: 4000 },
      { type: 'costa_rica', description: 'Plan residency transition for foreign income exclusion', potential: 15000 }
    ],
    // Ready to Invest (wealth → stock flow)
    investableData: null
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchProperties = async () => {
    try {
      // Debug: Check what's available
      const currentToken = localStorage.getItem('accessToken');
      console.log('🔍 Dashboard fetchProperties - Token from localStorage:', currentToken);
      
      // Use centralized API client
      const response = await api.get('/api/properties');
      if (response.data.success) {
        setDashboardData(prev => ({
          ...prev,
          properties: response.data.data
        }));
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const taxYear = new Date().getFullYear();

      const [
        propertiesRes,
        accountsRes,
        taxDashRes,
        expenseSummaryRes,
        taxableSummaryRes,
        investableRes
      ] = await Promise.all([
        api.get('/api/properties'),
        api.get('/api/accounts'),
        api.get(`/api/tax/dashboard/${taxYear}`),
        api.get(`/api/expenses/summary?tax_year=${taxYear}`),
        api.get(`/api/taxable-accounts/summary?taxYear=${taxYear}`).catch(() => ({ data: { success: true, data: {} } })),
        api.get(`/api/wealth/investable?taxYear=${taxYear}`).catch(() => ({ data: { success: false } }))
      ]);

      const properties = propertiesRes.data?.data || [];
      const accounts = accountsRes.data?.data || [];
      const taxDash = taxDashRes.data?.data || {};
      const expSummary = expenseSummaryRes.data?.data || {};
      const taxableSummary = taxableSummaryRes.data?.data || {};

      const investableData = investableRes.data?.success ? investableRes.data.data : null;

      setDashboardData(prev => ({
        ...prev,
        // Tax Summary
        currentYearTax: taxDash.summary?.totalExpenses ? (taxDash.latestCalculation?.total_tax || 0) : (taxDash.latestCalculation?.total_tax || 0),
        projectedTax: taxDash.latestCalculation?.projected_tax || taxDash.latestCalculation?.total_tax || 0,
        taxSavings: expSummary.deductible_expenses || 0,
        // Real Estate Portfolio
        properties,
        // Account Overview - now using real data
        accounts,
        // Ready to Invest
        investableData
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getTotalRealEstateProfit = () => {
    return dashboardData.properties.reduce((sum, property) => {
      const income = property.monthly_rent * 12 || 0;
      const expenses = property.monthly_expenses * 12 || 0;
      return sum + (income - expenses);
    }, 0);
  };

  const getTotalAccountBalance = () => {
    return dashboardData.accounts.reduce((sum, account) => sum + account.balance, 0);
  };

  const getTotalRealEstateValue = () => {
    return dashboardData.properties.reduce((sum, property) => sum + (property.property_value || 0), 0);
  };

  const getTotalAssets = () => {
    return getTotalAccountBalance() + getTotalRealEstateValue();
  };

  const getTotalLiabilities = () => {
    // Sum up all loan amounts from properties
    return dashboardData.properties.reduce((sum, property) => sum + (property.loan_amount || 0), 0);
  };

  const getNetAssets = () => {
    return getTotalAssets() - getTotalLiabilities();
  };

  const getTotalOpportunities = () => {
    return dashboardData.opportunities.reduce((sum, opportunity) => sum + opportunity.potential, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <StructuredData type="application" page="dashboard" />
      <CanonicalURL />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Optimization Dashboard</h1>
            <p className="text-gray-600">Your personalized overview of tax optimization opportunities and portfolio status</p>
            <Disclaimer variant="compact" />
          </div>
          <Link
            to="/property-manager"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Building className="h-4 w-4 mr-2" />
            Manage Properties
          </Link>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingDown className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Tax Savings</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(dashboardData.taxSavings)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Real Estate Profit</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(getTotalRealEstateProfit())}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Years to Roth Qualified</p>
              <p className="text-2xl font-semibold text-gray-900">{dashboardData.yearsUntilQualified}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowAssetBreakdown(true)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Assets</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(getTotalAssets())}</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowNetAssetBreakdown(true)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calculator className="h-8 w-8 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Net Assets</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(getNetAssets())}</p>
                <p className="text-xs text-gray-500">Assets - Liabilities</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'realestate', name: 'Real Estate', icon: Building },
            { id: 'roth', name: 'Roth IRA', icon: PiggyBank },
            { id: 'accounts', name: 'Accounts', icon: CreditCard },
            { id: 'opportunities', name: 'Opportunities', icon: Target }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="inline h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Ready to Invest - Wealth → Stock Flow */}
            {dashboardData.investableData && dashboardData.investableData.investableAmount > 0 && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg shadow-sm border-2 border-emerald-200 p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-emerald-100 rounded-full p-3">
                      <Wallet className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-xl font-bold text-gray-900">Ready to Invest</h2>
                      <p className="text-2xl font-bold text-emerald-700 mt-1">
                        {formatCurrency(dashboardData.investableData.investableAmount)} available
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Based on income, tax liability, and essential expenses for {dashboardData.investableData.taxYear}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={`/individual-stock-analysis?investAmount=${Math.round(dashboardData.investableData.investableAmount)}&risk=moderate`}
                    className="inline-flex items-center px-5 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                  >
                    Explore stocks that match your risk profile
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              <div className="space-y-3">
                {dashboardData.recentExpenses.map((expense, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-900">{expense.description}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(expense.amount)}</p>
                      <p className="text-xs text-gray-500">{expense.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link
                to="/tax-forms"
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <Calculator className="h-8 w-8 text-blue-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tax Forms</h3>
                <p className="text-sm text-gray-600">Complete your return, track deductions, export PDF</p>
              </Link>

              <Link
                to="/expense-tracker"
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <FileText className="h-8 w-8 text-green-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Expenses</h3>
                <p className="text-sm text-gray-600">Monitor deductible expenses</p>
              </Link>

              <Link
                to="/roth-ira-planner"
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <PiggyBank className="h-8 w-8 text-purple-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Roth Planning</h3>
                <p className="text-sm text-gray-600">Optimize Roth IRA strategy</p>
              </Link>

              <Link
                to="/individual-stock-analysis"
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <TrendingUp className="h-8 w-8 text-indigo-600 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Stock Analysis</h3>
                <p className="text-sm text-gray-600">Individual stock analysis and screening</p>
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'realestate' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Real Estate Portfolio</h2>
                <Link
                  to="/property-manager"
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Building className="h-4 w-4 mr-2" />
                  Manage Properties
                </Link>
              </div>
              
              {dashboardData.properties.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {dashboardData.properties.map((property) => (
                    <div key={property.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {property.type === 'rental' ? (
                            <Building className="h-5 w-5 text-blue-600" />
                          ) : property.type === 'primary' ? (
                            <Home className="h-5 w-5 text-green-600" />
                          ) : (
                            <Home className="h-5 w-5 text-purple-600" />
                          )}
                          <span className="text-sm font-medium text-gray-500 capitalize">
                            {property.type}
                          </span>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          property.status === 'active' ? 'bg-green-100 text-green-800' :
                          property.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {property.status}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{property.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{property.address}, {property.city}, {property.state}</p>
                      <div className="space-y-2">
                        {property.property_value > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Value:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(property.property_value)}</span>
                          </div>
                        )}
                        {property.monthly_rent > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Monthly Rent:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(property.monthly_rent)}</span>
                          </div>
                        )}
                        {property.monthly_expenses > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Monthly Expenses:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(property.monthly_expenses)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No properties found</h3>
                  <p className="mt-1 text-sm text-gray-500">Add your first property to start tracking your real estate portfolio.</p>
                  <div className="mt-6">
                    <Link
                      to="/property-manager"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Building className="h-4 w-4 mr-2" />
                      Add Property
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'roth' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Roth IRA Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Balance:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(dashboardData.rothBalance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Contributions:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(dashboardData.rothContributions)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Conversions:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(dashboardData.rothConversions)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Earnings:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(dashboardData.rothEarnings)}</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-2">Years Until Qualified</h3>
                  <p className="text-2xl font-bold text-blue-600">{dashboardData.yearsUntilQualified}</p>
                  <p className="text-sm text-blue-700">Until age 59.5 for penalty-free withdrawals</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-medium text-green-900 mb-2">Strategy Status</h3>
                  <p className="text-sm text-green-700">Large Roth IRA provides significant tax-free income potential</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Management</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.accounts.map((account, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    {account.type === 'personal' ? (
                      <CreditCard className="h-5 w-5 text-blue-600" />
                    ) : account.type === 'rental' ? (
                      <Building className="h-5 w-5 text-green-600" />
                    ) : account.type === 'investment' ? (
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    ) : (
                      <Globe className="h-5 w-5 text-orange-600" />
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">{account.name}</h3>
                      <p className="text-xs text-gray-500 capitalize">{account.type}</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(account.balance)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'opportunities' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax Optimization Opportunities</h2>
              <div className="space-y-4">
                {dashboardData.opportunities.map((opportunity, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                    <div className="flex-shrink-0">
                      {opportunity.type === 'roth_conversion' ? (
                        <PiggyBank className="h-6 w-6 text-purple-600" />
                      ) : opportunity.type === 'real_estate' ? (
                        <Building className="h-6 w-6 text-blue-600" />
                      ) : opportunity.type === 'family_credits' ? (
                        <Home className="h-6 w-6 text-green-600" />
                      ) : (
                        <Globe className="h-6 w-6 text-orange-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{opportunity.description}</h3>
                      <p className="text-sm text-gray-500 mt-1">Potential savings: {formatCurrency(opportunity.potential)}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Active
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Action Required</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Review your property expenses and Roth IRA strategy to maximize tax savings this year.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Asset Breakdown Modal */}
        {showAssetBreakdown && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Total Assets Breakdown</h2>
                <button onClick={() => setShowAssetBreakdown(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900">Account Balances</h3>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalAccountBalance())}</p>
                  <div className="mt-2 space-y-1">
                    {dashboardData.accounts.map(account => (
                      <div key={account.id} className="flex justify-between text-sm">
                        <span>{account.name}</span>
                        <span>{formatCurrency(account.balance)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-medium text-green-900">Property Values</h3>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(getTotalRealEstateValue())}</p>
                  <div className="mt-2 space-y-1">
                    {dashboardData.properties.map(property => (
                      <div key={property.id} className="flex justify-between text-sm">
                        <span>{property.name}</span>
                        <span>{formatCurrency(property.property_value || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-medium text-purple-900">Total Assets</h3>
                  <p className="text-3xl font-bold text-purple-600">{formatCurrency(getTotalAssets())}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Net Asset Breakdown Modal */}
        {showNetAssetBreakdown && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Net Assets Breakdown</h2>
                <button onClick={() => setShowNetAssetBreakdown(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-900">Total Assets</h3>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalAssets())}</p>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-medium text-red-900">Total Liabilities</h3>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(getTotalLiabilities())}</p>
                  <div className="mt-2 space-y-1">
                    {dashboardData.properties.map(property => (
                      <div key={property.id} className="flex justify-between text-sm">
                        <span>{property.name} Loan</span>
                        <span>{formatCurrency(property.loan_amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-medium text-green-900">Net Assets</h3>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(getNetAssets())}</p>
                  <p className="text-sm text-green-700 mt-1">Assets - Liabilities</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Dashboard;
